import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClbTag } from '@celebration/react/ClbTag/ClbTag';
import ITableHead from '../../../../common/components/table/interfaces/ITableHead';
import Table from '../../../../common/components/table';
import IPagination from '../../../../common/interfaces/IPagination';
import ReportsConstants from '../../constants';
import IReportsHistoricResponse from '../../../../common/interfaces/reports/IReportsHistoricResponse';
import IReportsHistoricDataTable from '../interfaces/IReportsHistoricDataTable';
import LanguageContext from '../../../../app/contexts/language';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import IReportsHistoricProps from './interfaces/IReportsHistoricProps';
import RoundManagerApiService from '../../../../common/services/roundmanager-api';
import i18n from '../../../../lib/i18n';

function ReportsHistoric({
    isGeneratingReport,
}: Readonly<IReportsHistoricProps>) {
    const { language } = LanguageContext.useLanguage();
    const { setToast } = useToast();

    const [isFetchingData, setIsFetchingData] = useState(false);

    const [historicDataTable, setHistoricDataTable] = useState<
        IPagination<IReportsHistoricDataTable>
    >(ReportsConstants.HISTORIC_DEFAULT_DATA);

    const currentLocale = useMemo(() => language ?? 'en-US', [language]);

    const getTableHeaders = (): ITableHead[] => {
        const headers: ITableHead[] = [
            { id: 'status', label: i18n.t('Status'), field: 'status' },
            {
                id: 'exportDate',
                label: i18n.t('DataExportacao'),
                field: 'exportDate',
            },
            { id: 'userName', label: i18n.t('Usuario'), field: 'userName' },
            { id: 'fileName', label: i18n.t('Arquivo'), field: 'fileName' },
        ];

        return headers;
    };

    const getStatusTag = useCallback(
        (index: number, status: number, statusDescription: string) => {
            switch (status) {
                case 1:
                    return (
                        <ClbTag id={`${index.toString()}-tag`} type="positive">
                            {statusDescription}
                        </ClbTag>
                    );
                case 2:
                default:
                    return (
                        <ClbTag id={`${index.toString()}-tag`} type="negative">
                            {statusDescription}
                        </ClbTag>
                    );
            }
        },
        [],
    );

    const mapHistoricToDataTable = useCallback(
        (data: IReportsHistoricResponse[]) => {
            return data.map((item, index) => {
                const exportDate = new Date(
                    item.exportDateTime,
                ).toLocaleDateString(currentLocale);
                const exportTime = new Date(
                    item.exportDateTime,
                ).toLocaleTimeString(currentLocale);

                return {
                    id: index.toString(),
                    status: getStatusTag(
                        index,
                        item.status,
                        item.statusDescription,
                    ),
                    exportDate: `${exportDate} ${exportTime}`,
                    userName: item.userName,
                    fileName: item.fileName,
                } as IReportsHistoricDataTable;
            });
        },
        [getStatusTag, currentLocale],
    );

    const dataTablePaginated = useCallback(
        (historicData: IPagination<IReportsHistoricResponse>) => {
            return {
                data: mapHistoricToDataTable(historicData.data),
            } as IPagination<IReportsHistoricDataTable>;
        },
        [mapHistoricToDataTable],
    );

    const fetchData = useCallback(async () => {
        setIsFetchingData(true);

        const { response, error } =
            await RoundManagerApiService.Reports.getHistoricListAsync(1, 5);

        if (response) setHistoricDataTable(dataTablePaginated(response));
        else {
            setToast('negative', i18n.t('Erro'), error?.message);
        }

        setIsFetchingData(false);
    }, [dataTablePaginated, setToast]);

    useEffect(() => {
        if (isGeneratingReport) return;

        fetchData();
    }, [fetchData, isGeneratingReport]);

    return (
        <Table
            hideToolbarTable
            headers={getTableHeaders()}
            content={historicDataTable}
            emptyTableDescription={i18n.t('NadaExibir')}
            disablePagination
            fetchingData={isFetchingData}
        />
    );
}

export default ReportsHistoric;
